import React, { useState, useEffect, useMemo } from 'react';
import { useWigTransfer } from '../contexts/WigTransferContext';
import './styles/WigTransferModal.css';
import { API_URL } from '../config/api';
import { toast } from 'react-toastify';
import { getCurrentUser } from '../utils/auth';
import { getCurrentTimestamp, toAPIDate } from '../utils/dateUtils';
import { getAvailableWorkers } from '../services/manufacturing';

const WigTransferModal = () => {
  const {
    isModalOpen,
    transferData,
    closeTransferModal,
    setSelectedToRoom,
    executeTransfer,
    canTransfer,
    hasValidationErrors,
    completeOrder,
    getCurrentUser  
  } = useWigTransfer();

  const currentUser = getCurrentUser();
  // Move hooks to the top, before any conditional returns
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [completedRooms, setCompletedRooms] = useState([]);
  const [selectedTargetRoom, setSelectedTargetRoom] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [targetRoomWorkers, setTargetRoomWorkers] = useState([]);
  const [selectedTargetWorker, setSelectedTargetWorker] = useState('');
  const [isLoadingTargetRoomWorkers, setIsLoadingTargetRoomWorkers] = useState(false);
  const [rejectRoomWorkers, setRejectRoomWorkers] = useState([]);
  const [isLoadingRejectRoomWorkers, setIsLoadingRejectRoomWorkers] = useState(false);
  const [rejectForm, setRejectForm] = useState({
    reason: '',
    assignedWorker: '',
    dueDate: ''
  });

  const {
    orderNo,
    currentRoom,
    availableRooms,
    selectedToRoom,
    isLoading,
    error,
    validationErrors,
    transferHistory
  } = transferData;

const getUnvisitedRooms = async () => {
  if (!availableRooms || availableRooms.length === 0) return availableRooms;
  
  console.log('🔍 Current Room for comparison:', currentRoom);
  console.log('🔍 Current Room TaskID:', currentRoom?.taskId || currentRoom?.ID || 'NO TASK ID FOUND');
  
  availableRooms.forEach(room => {
    console.log(`   Room ${room.roomNo}: Task=${room.roomDescription}, TaskID=${room.taskId || room.ID}, Status=${room.currentTaskStatus}`);
  });
  
  const unvisitedRooms = availableRooms.filter(room => {
    // Get the unique task identifiers
    const roomTaskId = room.taskId || room.ID;
    const currentTaskId = currentRoom?.taskId || currentRoom?.ID;

    console.log(`🔍 Comparing Task IDs: ${roomTaskId} vs ${currentTaskId} for room ${room.roomNo}`);

    // Only exclude if we have exact task ID match (this would need currentRoom to have proper task ID)
    if (currentTaskId && roomTaskId && roomTaskId === currentTaskId) {
      console.log(`❌ Task ID ${roomTaskId} excluded - exact task match`);
      return false;
    }

    // Exclude room+task combinations with client_reject status
    if (
      room.hasClientReject === true || 
      (typeof room.currentTaskStatus === 'string' && room.currentTaskStatus.toLowerCase().includes('reject'))
    ) {
      console.log(`❌ Task ID ${roomTaskId} excluded - client reject or rejected status (${room.currentTaskStatus})`);
      return false;
    }

    // Exclude if this specific task is completed
    if (room.currentTaskStatus === 'Task Completed') {
      console.log(`❌ Task ID ${roomTaskId} excluded - task status completed`);
      return false;
    }

    // Allow all other tasks (including same room, different tasks)
    console.log(`✅ Task ID ${roomTaskId} available for transfer`);
    return true;
  });

  console.log(`📊 Filtering results: ${availableRooms.length} total tasks → ${unvisitedRooms.length} available for transfer`);
  
  return unvisitedRooms;
};

  // Add this useEffect to filter rooms when data changes
  useEffect(() => {
    const filterRooms = async () => {
      if (availableRooms && availableRooms.length > 0 && currentRoom) {
        const unvisited = await getUnvisitedRooms();
        setFilteredRooms(unvisited);
      } else {
        setFilteredRooms([]);
      }
    };
    
    filterRooms();
  }, [availableRooms, currentRoom, orderNo]);

  // Load workers assigned to the selected "To" room, same pattern as
  // workerOptions in RoomManufacturingWithContext.jsx (getAvailableWorkers by roomNo)
  useEffect(() => {
    setSelectedTargetWorker('');

    if (!selectedToRoom) {
      setTargetRoomWorkers([]);
      return;
    }

    const [targetRoomNo] = selectedToRoom.split('-');
    let isCurrent = true;

    const loadTargetRoomWorkers = async () => {
      setIsLoadingTargetRoomWorkers(true);
      try {
        const workers = await getAvailableWorkers(targetRoomNo);
        if (isCurrent) setTargetRoomWorkers(workers);
      } catch (err) {
        console.error('Failed to load workers for target room:', err);
        if (isCurrent) setTargetRoomWorkers([]);
      } finally {
        if (isCurrent) setIsLoadingTargetRoomWorkers(false);
      }
    };

    loadTargetRoomWorkers();

    // Ignore this request's result if selectedToRoom changes again before it resolves
    return () => {
      isCurrent = false;
    };
  }, [selectedToRoom]);

  const workerOptions = useMemo(() => {
    return [...targetRoomWorkers].sort((a, b) => a.name.localeCompare(b.name));
  }, [targetRoomWorkers]);

  // Load workers assigned to the selected reject/rework room, same pattern as above
  useEffect(() => {
    if (!selectedTargetRoom) {
      setRejectRoomWorkers([]);
      return;
    }

    let isCurrent = true;

    const loadRejectRoomWorkers = async () => {
      setIsLoadingRejectRoomWorkers(true);
      try {
        const workers = await getAvailableWorkers(selectedTargetRoom);
        if (isCurrent) setRejectRoomWorkers(workers);
      } catch (err) {
        console.error('Failed to load workers for reject room:', err);
        if (isCurrent) setRejectRoomWorkers([]);
      } finally {
        if (isCurrent) setIsLoadingRejectRoomWorkers(false);
      }
    };

    loadRejectRoomWorkers();

    return () => {
      isCurrent = false;
    };
  }, [selectedTargetRoom]);

  const rejectWorkerOptions = useMemo(() => {
    return [...rejectRoomWorkers].sort((a, b) => a.name.localeCompare(b.name));
  }, [rejectRoomWorkers]);

  // Fetch previous room when reject modal opens
  useEffect(() => {
    if (showRejectModal && orderNo) {
      fetchCompletedRooms();
    }
  }, [showRejectModal, orderNo]);

  // Auto-fill assigned worker when a target room is selected
  useEffect(() => {
    if (selectedTargetRoom && completedRooms.length > 0) {
      const selectedRoom = completedRooms.find(r => r.roomNo == selectedTargetRoom);
      if (selectedRoom?.assignedWorker) {
        setRejectForm(prev => ({
          ...prev,
          assignedWorker: selectedRoom.assignedWorker
        }));
      }
    }
  }, [selectedTargetRoom, completedRooms]);

  // Now the conditional return comes after all hooks
  if (!isModalOpen) return null;

const fetchCompletedRooms = async () => {
  try {
    const response = await fetch(`${API_URL}/api/orders/${orderNo}/available-reject-rooms`);
    if (response.ok) {
      const data = await response.json();
      setCompletedRooms(data.availableRooms || []);
      console.log(`📋 Available reject rooms:`, data.availableRooms);
    } else {
      const errorData = await response.json();
      toast.error(`Cannot get available rooms: ${errorData.message}`);
      setCompletedRooms([]);
    }
  } catch (error) {
    console.error('Error fetching available reject rooms:', error);
    toast.error('Failed to fetch available rooms for rejection');
    setCompletedRooms([]);
  }
};


  const handleTransfer = async () => {
    const result = await executeTransfer(selectedTargetWorker);
    
    if (result.success) {
      // Enhanced success toast with transfer details
      toast.success(
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            ✅ Transfer Completed Successfully!
          </div>
          <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
            📤 Scanned OUT of Room {result.transferDetails.fromRoom}<br/>
            📥 Scanned IN to Room {result.transferDetails.toRoom}<br/>
            👤 Transferred by: {result.transferDetails.transferredBy}<br/>
            🕐 Time: {new Date(result.transferDetails.transferDateTime).toLocaleString()}
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
      
      closeTransferModal();
      
      // Dispatch event to refresh parent component data
      window.dispatchEvent(new CustomEvent('wigTransferCompleted', {
        detail: { 
          orderNo, 
          newCurrentRoom: result.newCurrentRoom,
          transferDetails: result.transferDetails
        }
      }));
      
    } else {
      // Handle validation errors or other failures
      if (result.errors && result.errors.length > 0) {
        toast.error(
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              Transfer Blocked
            </div>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '0.9em' }}>
              {result.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>,
          {
            position: "top-right",
            autoClose: 7000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else {
        toast.error(result.error || 'Transfer failed. Please try again.', {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  };

  const handleCompleteOrder = async () => {    
    const result = await completeOrder(orderNo);
    
    if (result.success) {
      toast.success(
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            ✅ Order Auto-Completed Successfully!
          </div>
          <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
            📋 Order #{orderNo} has been marked as complete<br/>
            📊 {result.data.tasksCompleted} tasks completed<br/>
            👤 Completed by: {currentUser}<br/>
            🕐 Time: {new Date(result.data.completedAt).toLocaleString()}
            {result.data.canUndo && <br/>}
            {result.data.canUndo && "🔄 Action can be undone if needed"}
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
      
      closeTransferModal();
      
      // Dispatch event to refresh parent component data
      window.dispatchEvent(new CustomEvent('wigTransferCompleted', {
        detail: { 
          orderNo, 
          action: 'auto-complete',
          completionDetails: result.data
        }
      }));
      
    } else {
      toast.error(result.error || 'Failed to complete order. Please try again.', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };


const handleReject = async () => {
  if (!rejectForm.reason.trim()) {
    toast.error('Please provide a reason for rejection');
    return;
  }

  if (!selectedTargetRoom) {
    toast.error('Please select a room to send the order back to');
    return;
  }

  if (!rejectForm.assignedWorker.trim()) {
    toast.error('Please assign a worker for the rework');
    return;
  }

  try {
    const currentUser = getCurrentUser();

    const selectedRoom = completedRooms.find(r => r.roomNo == selectedTargetRoom);
    const isSameRoom = selectedRoom?.isCurrentRoom;

    // Only include assignedWorker if user explicitly selected one
    const requestBody = {
      rejectedBy: currentUser,
      rejectReason: rejectForm.reason,
      dueDate: rejectForm.dueDate || null,
      targetRoomNo: selectedTargetRoom,
      rejectedDateTime: getCurrentTimestamp()
    };

    // Only add assignedWorker if user explicitly selected one (not empty or default)
    if (rejectForm.assignedWorker &&
        rejectForm.assignedWorker.trim() !== '' &&
        rejectForm.assignedWorker !== 'Select Worker') {
      requestBody.assignedWorker = rejectForm.assignedWorker;
    }

    const response = await fetch(`${API_URL}/api/orders/${orderNo}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    const result = await response.json();

    if (result.success) {
      const toastMessage = isSameRoom ? (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            🔄 Task Flagged for Rework!
          </div>
          <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
            📍 Room {result.rejectDetails.fromRoom} - Task flagged for rework<br/>
            👤 Flagged by: {result.rejectDetails.rejectedBy}<br/>
            📝 Reason: {result.rejectDetails.rejectReason}
            {result.rejectDetails.assignedWorker && <><br/>👷 Assigned to: {result.rejectDetails.assignedWorker}</>}
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            🚫 Order Rejected Successfully!
          </div>
          <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
            📤 Rejected from Room {result.rejectDetails.fromRoom}<br/>
            📥 Sent back to Room {result.rejectDetails.backToRoom}<br/>
            👤 Rejected by: {result.rejectDetails.rejectedBy}<br/>
            📝 Reason: {result.rejectDetails.rejectReason}
            {result.rejectDetails.assignedWorker && <><br/>👷 Assigned to: {result.rejectDetails.assignedWorker}</>}
          </div>
        </div>
      );

      toast.success(toastMessage, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setShowRejectModal(false);
      closeTransferModal();

      // Dispatch event to refresh parent component data
      window.dispatchEvent(new CustomEvent('wigTransferCompleted', {
        detail: { 
          orderNo, 
          newCurrentRoom: result.newCurrentRoom,
          transferDetails: result.rejectDetails,
          action: 'reject'
        }
      }));
      
    } else {
      toast.error(result.message || 'Reject failed. Please try again.');
    }
  } catch (error) {
    console.error('Error rejecting order:', error);
    toast.error('Failed to reject order. Please try again.');
  }
};
const formatRoomDisplay = (room) => {
  if (!room) return 'Not assigned';
  
  // Use the same pattern as filteredRooms mapping: room.roomDescription || 'Unknown Task'
  const taskDescription = room.roomDescription || 'Unknown Task';
  
  // Format: "Room {roomNo} - {taskDescription}"
  let display = `Room ${room.roomNo} - ${taskDescription}`;
  
  // Add assigned worker if available (following the select pattern)
  if (room.assignedWorker && room.assignedWorker.trim() !== '') {
    display += ` (${room.assignedWorker})`;
  }
  
  return display;
};



  const handleRejectFormChange = (field, value) => {
    setRejectForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openRejectModal = () => {
    setShowRejectModal(true);
    setRejectForm({
      reason: '',
      assignedWorker: '',
      dueDate: ''
    });
    setSelectedTargetRoom('');
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setCompletedRooms([]);
    setSelectedTargetRoom(''); // ADD THIS LINE
    setRejectForm({
      reason: '',
      assignedWorker: '',
      dueDate: ''
    });
  };

  return (
    <div className="transfer-modal-overlay" onClick={closeTransferModal}>
      <div className="transfer-modal-content" onClick={(e) => e.stopPropagation()}>
        
        {/* Modal Header */}
        <div className="transfer-modal-header">
          <h3>
            <i className="fas fa-exchange-alt mr-2"></i>
            Transfer Wig - Order #{orderNo}
          </h3>
          <button 
            className="transfer-modal-close"
            onClick={closeTransferModal}
            aria-label="Close modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="transfer-modal-body">
          
          {/* Loading State */}
          {isLoading && (
            <div className="transfer-loading">
              <div className="loading-spinner"></div>
              <span>Loading transfer information...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="transfer-error">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
          )}

          {/* Current Room Information */}
          {currentRoom && (
            <div className="current-room-section">
              <h4>Current Location</h4>
              <div className="current-room-info">
                <div className="room-details">
                  <span className="room-badge">
                    Room {currentRoom.roomNo}
                  </span>
                  {/* <span className="room-name">
                    {currentRoom.roomTask || currentRoom.currentTask || currentRoom.roomDescription || 'Unknown Task'}
                  </span> */}
                  <span className="room-location">
                    {currentRoom.location}
                  </span>
                </div>
                
              
              </div>
            </div>
          )}

          {/* Transfer Controls */}
          <div className="transfer-controls-section">
            <h4>Transfer Destination</h4>
            
            <div className="transfer-flow">
              <div className="transfer-from">
                <span className="transfer-label">From:</span>
                <span className="room-display">
                  {formatRoomDisplay(currentRoom)}
                </span>
              </div>
              
              <div className="transfer-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
              
              <div className="transfer-to">
                <span className="transfer-label">To:</span>
                <select
  className="room-select"
  value={selectedToRoom}
  onChange={(e) => {
    const value = e.target.value;
    setSelectedToRoom(value);
    
    if (value) {
      const parts = value.split('-');
      const taskId = parts[1];
      setSelectedTaskId(taskId);
      console.log(`🎯 Selected: Room ${parts[0]}, Task ID ${taskId}`);
    } else {
      setSelectedTaskId('');
    }
  }}
  disabled={isLoading || !availableRooms.length}
>
                  <option value="">Select Target Room + Task</option>
                  {filteredRooms.map(room => (
                    <option 
                      key={`${room.roomNo}-${room.taskId || room.ID}`} 
                      value={`${room.roomNo}-${room.taskId || room.ID}`}
                    >
                      Room {room.roomNo} - {room.roomDescription || 'Unknown Task'}
                      {room.assignedWorker && ` (${room.assignedWorker})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Optional: assign a worker from the selected "To" room */}
            {selectedToRoom && (
              <div className="transfer-worker-section" style={{ marginTop: '12px' }}>
                <div className="transfer-to">
                  <span className="transfer-label">Assign Worker (optional):</span>
                  <select
                    className="room-select"
                    value={selectedTargetWorker}
                    onChange={(e) => setSelectedTargetWorker(e.target.value)}
                    disabled={isLoading || isLoadingTargetRoomWorkers}
                  >
                    <option value="">
                      {isLoadingTargetRoomWorkers ? 'Loading workers...' : 'No specific worker'}
                    </option>
                    {workerOptions.map(worker => (
                      <option key={worker.id} value={worker.name}>
                        {worker.name}
                      </option>
                    ))}
                  </select>
                </div>
                {!isLoadingTargetRoomWorkers && workerOptions.length === 0 && (
                  <small className="form-help">No workers found for this room.</small>
                )}
              </div>
            )}

            {/* No available rooms message */}
            {!isLoading && filteredRooms.length === 0 && availableRooms.length > 0 && (
              <div className="no-rooms-message">
                <i className="fas fa-info-circle mr-2"></i>
                No unvisited rooms available for transfer. This wig has already been to all available rooms.
              </div>
            )}
          </div>

        

          {/* Validation Errors */}
          {hasValidationErrors && (
            <div className="validation-errors">
              <h5>
                <i className="fas fa-exclamation-triangle mr-2"></i>
                Transfer Blocked
              </h5>
              <ul>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Transfer History */}
          {transferHistory.length > 0 && (
            <div className="transfer-history-section">
              <h4>
                <button 
                  className="history-toggle"
                  onClick={() => {
                    const historyList = document.querySelector('.transfer-history-list');
                    historyList.style.display = historyList.style.display === 'none' ? 'block' : 'none';
                  }}
                >
                  <i className="fas fa-history mr-1"></i>
                  Transfer History ({transferHistory.length})
                  <i className="fas fa-chevron-down ml-1"></i>
                </button>
              </h4>
              
              <div className="transfer-history-list" style={{ display: 'none' }}>
                {transferHistory.slice(0, 5).map((transfer, index) => (
                  <div key={index} className="history-item">
                    <div className="history-route">
                      <span className="from-room">Room {transfer.fromRoom}</span>
                      <i className="fas fa-arrow-right mx-2"></i>
                      <span className="to-room">Room {transfer.toRoom}</span>
                    </div>
                    <div className="history-details">
                      <span className="transfer-date">
                        {new Date(transfer.transferDate).toLocaleDateString()}
                      </span>
                      <span className="transferred-by">by {transfer.transferredBy}</span>
                    </div>
                  </div>
                ))}
                {transferHistory.length > 5 && (
                  <div className="history-more">
                    +{transferHistory.length - 5} more transfers
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer */}      
        <div className="transfer-modal-footer">
          {!isLoading && filteredRooms.length === 0 && availableRooms.length > 0 && (
            <div className="complete-order-section">
            
              <button 
                className="btn btn-primary"
                onClick={handleCompleteOrder}
                disabled={isLoading}
              >
                <i className="fas fa-check-circle mr-1"></i>
                Complete Order
              </button>
            </div>
          )}
          <button 
            className="btn btn-secondary"
            onClick={closeTransferModal}
            disabled={isLoading}
          >
            Cancel
          </button>
          
          {/* Reject Button */}
          <button 
            className="btn btn-danger"
            onClick={openRejectModal}
            disabled={isLoading || !currentRoom}
            title="Reject and send back to previous room"
          >
            <i className="fas fa-times-circle mr-1"></i>
            Reject
          </button>
          
          <button
            className="btn btn-primary"
            onClick={handleTransfer}
            disabled={!canTransfer || isLoading || hasValidationErrors}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-1"></i>
                Transferring...
              </>
            ) : (
              <>
                <i className="fas fa-exchange-alt mr-1"></i>
                Transfer Wig
              </>
            )}
          </button>
        </div>

      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="reject-modal-overlay"
          onClick={(e) => {
            e.stopPropagation();
            closeRejectModal();
          }}
        >
          <div className="reject-modal-content" onClick={(e) => e.stopPropagation()}>
            
            {/* Reject Modal Header */}
            <div className="reject-modal-header">
              <h3>
                <i className="fas fa-times-circle mr-2"></i>
                Reject Order #{orderNo}
              </h3>
              <button 
                className="reject-modal-close"
                onClick={closeRejectModal}
                aria-label="Close reject modal"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Reject Modal Body */}
            <div className="reject-modal-body">
              
              {/* Target Room Selection */}
              <div className="reject-info-section">
                <h4>Reject Details</h4>
                <div className="reject-flow">
                  <div className="reject-from">
                    <span className="reject-label">Rejecting from:</span>
                    <span className="room-display current-room">
                      Room {currentRoom?.roomNo}
                    </span>
                  </div>
                  
                  <div className="reject-arrow">
                    <i className="fas fa-undo"></i>
                  </div>
                  
<div className="reject-to">
  <span className="reject-label">Send back to:</span>
  <select
    className="form-control room-select"
    value={selectedTargetRoom}
    onChange={(e) => setSelectedTargetRoom(e.target.value)}
    required
  >
    <option value="">Select room for rework...</option>
    
    {completedRooms.map(room => (
      <option 
        key={room.taskId} 
        value={room.roomNo}
        className={room.isCurrentRoom ? 'current-room-option' : ''}
      >
        {room.isCurrentRoom ? '🔄 ' : ''}
        Room {room.roomNo} - {room.roomTask}
        {room.isCurrentRoom ? ' (CURRENT - Rework here)' : ''}
        {room.assignedWorker && ` (${room.assignedWorker})`}
      </option>
    ))}
  </select>
</div>
                </div>

                {completedRooms.length === 0 && (
                  <div className="no-completed-rooms-message">
                    <i className="fas fa-info-circle mr-2"></i>
                    No completed rooms available for rejection. This order may not have any completed tasks yet.
                  </div>
                )}

{selectedTargetRoom && (
  <div className="selected-room-info">
    {(() => {
      const selectedRoom = completedRooms.find(r => r.roomNo == selectedTargetRoom);
      const isSameRoom = selectedRoom?.isCurrentRoom;
      
      return selectedRoom ? (
        <div className={`room-selection-info ${isSameRoom ? 'same-room' : 'previous-room'}`}>
          {isSameRoom ? (
            <div className="same-room-reject-info">
              <i className="fas fa-redo mr-2"></i>
              <strong>Same Room Rework:</strong> This will flag the current task for rework without moving the wig to another room.
              <div className="room-details">
                <span>Current Task: {selectedRoom.roomTask}</span>
                <span>Status: {selectedRoom.taskStatus}</span>
              </div>
            </div>
          ) : (
            <div className="previous-room-reject-info">
              <i className="fas fa-undo mr-2"></i>
              <strong>Previous Room Return:</strong> This will send the wig back to a previously worked room for rework.
              <div className="room-details">
                <span>Task: {selectedRoom.roomTask}</span>
                <span>Last Status: {selectedRoom.taskStatus}</span>
                {selectedRoom.assignedWorker && <span>Last worked by: {selectedRoom.assignedWorker}</span>}
                {selectedRoom.scanOutDate && (
                  <span>Last completed: {new Date(selectedRoom.scanOutDate).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          )}
        </div>
      ) : null;
    })()}
  </div>
)}
              </div>

              {/* Reject Form */}
              <div className="reject-form-section">
                
                {/* Rejection Reason */}
                <div className="form-group">
                  <label htmlFor="rejectReason" className="appointment-form-label required">
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    Rejection Reason
                  </label>
                  <textarea
                    id="rejectReason"
                    className="form-control"
                    placeholder="Please explain why this order is being rejected..."
                    value={rejectForm.reason}
                    onChange={(e) => handleRejectFormChange('reason', e.target.value)}
                    rows={3}
                    required
                  />
                </div>

                {/* Assigned Worker */}
                <div className="form-group">
                  <label htmlFor="assignedWorker" className="appointment-form-label required">
                    <i className="fas fa-user mr-1"></i>
                    Assign to Worker
                  </label>
                  <select
                    id="assignedWorker"
                    className="form-control"
                    value={rejectForm.assignedWorker}
                    onChange={(e) => handleRejectFormChange('assignedWorker', e.target.value)}
                    disabled={!selectedTargetRoom || isLoadingRejectRoomWorkers}
                    required
                  >
                    <option value="">
                      {!selectedTargetRoom
                        ? 'Select a room first'
                        : isLoadingRejectRoomWorkers
                        ? 'Loading workers...'
                        : 'Select worker...'}
                    </option>
                    {rejectForm.assignedWorker &&
                      !rejectWorkerOptions.some(w => w.name === rejectForm.assignedWorker) && (
                        <option value={rejectForm.assignedWorker}>
                          {rejectForm.assignedWorker} (previously assigned)
                        </option>
                      )}
                    {rejectWorkerOptions.map(worker => (
                      <option key={worker.id} value={worker.name}>
                        {worker.name}
                      </option>
                    ))}
                  </select>
                  {selectedTargetRoom && !isLoadingRejectRoomWorkers && rejectWorkerOptions.length === 0 && (
                    <small className="form-help">No workers found for this room.</small>
                  )}
                </div>

                {/* Due Date */}
                <div className="form-group">
                  <label htmlFor="dueDate" className="appointment-form-label">
                    <i className="fas fa-calendar mr-1"></i>
                    Due Date (Optional)
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    className="form-control"
                    value={rejectForm.dueDate}
                    onChange={(e) => handleRejectFormChange('dueDate', e.target.value)}
                    min={toAPIDate(new Date())}
                  />
                  <small className="form-help">
                    Set a target completion date for the rework
                  </small>
                </div>

              </div>

              

            </div>

            {/* Reject Modal Footer */}
            <div className="reject-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={closeRejectModal}
              >
                Cancel
              </button>
              
              <button 
                className="btn btn-danger"
                onClick={handleReject}
                disabled={!rejectForm.reason.trim() || !selectedTargetRoom || !rejectForm.assignedWorker.trim()}
              >
                <i className="fas fa-times-circle mr-1"></i>
                Confirm Reject
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default WigTransferModal;